import api from './api';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface AccessibleCompany {
    id: string;
    companyId: string;
    fantasyName: string;
    legalName: string;
    logoUrl: string | null;
    role: 'owner' | 'admin' | 'operator' | 'collaborator' | string;
    isPrimary: boolean;
    isCollaborator: boolean;
    status: string;
    kycStatus: 'pending' | 'verified' | 'rejected' | null;
}

export interface LoginResponse {
    success: boolean;
    message: string;
    user: {
        id: string;
        name: string;
        email: string;
        phone: string | null;
        status: string;
        companyId: string | null;
        hasCompany: boolean;
    };
    token: string;
    activeCompany?: AccessibleCompany;
    companies?: AccessibleCompany[];
}

// Interface para a resposta da verificação de e-mail
export interface VerifyEmailResponse {
    success: boolean;
    message: string;
}

// Interface para a resposta da recuperação de senha
export interface ForgotPasswordResponse {
    success: boolean;
    message: string;
}

// Interface para o payload do token JWT decodificado
interface JwtPayload {
    id?: string;
    userId?: string;
    email?: string;
    companyId?: string;
    exp?: number;
    iat?: number;
}

export const authService = {
    // Login do usuário
    login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
        const response = await api.post('/api/auth/login', credentials);
        return response.data;
    },

    // Salvar token e informações do usuário
    saveAuthData: (data: LoginResponse): void => {
        localStorage.clear();

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Salvar companyId separadamente para fácil acesso
        if (data.user.companyId) {
            localStorage.setItem('companyId', data.user.companyId);
        }

        // Multi-company: lista acessível + empresa ativa
        if (data.companies) {
            localStorage.setItem('companies', JSON.stringify(data.companies));
        }
        if (data.activeCompany) {
            localStorage.setItem('activeCompany', JSON.stringify(data.activeCompany));
        }

        // Limpar seleção de datas anterior para resetar para "hoje" no próximo acesso
        localStorage.removeItem('dateRangeSelection');
        localStorage.removeItem('dateRangePeriod');

        // Disparar evento de login para atualizar contextos
        window.dispatchEvent(new CustomEvent('authChange', { detail: { type: 'login' } }));
    },

    // Lista de empresas acessíveis (cache local)
    getCompanies: (): AccessibleCompany[] => {
        const raw = localStorage.getItem('companies');
        if (!raw) return [];
        try {
            return JSON.parse(raw) as AccessibleCompany[];
        } catch {
            return [];
        }
    },

    // Empresa ativa (cache local)
    getActiveCompany: (): AccessibleCompany | null => {
        const raw = localStorage.getItem('activeCompany');
        if (!raw) return null;
        try {
            return JSON.parse(raw) as AccessibleCompany;
        } catch {
            return null;
        }
    },

    // Buscar/atualizar lista de empresas acessíveis
    fetchMyCompanies: async (): Promise<{ activeCompanyId: string; companies: AccessibleCompany[] }> => {
        const response = await api.get('/api/auth/me/companies');
        const payload = response.data?.data ?? response.data;
        const companies: AccessibleCompany[] = payload?.companies ?? [];
        const activeCompanyId: string = payload?.activeCompanyId ?? '';

        localStorage.setItem('companies', JSON.stringify(companies));
        const active = companies.find((c) => c.id === activeCompanyId) ?? null;
        if (active) {
            localStorage.setItem('activeCompany', JSON.stringify(active));
        }

        return { activeCompanyId, companies };
    },

    // Trocar a empresa ativa (backend reemite o JWT)
    switchCompany: async (companyId: string): Promise<AccessibleCompany> => {
        const response = await api.post('/api/auth/switch-company', { companyId });
        const payload = response.data?.data ?? response.data;
        const newToken: string = payload?.token;
        const activeCompany: AccessibleCompany = payload?.activeCompany;

        if (newToken) {
            localStorage.setItem('token', newToken);
        }
        if (activeCompany) {
            localStorage.setItem('activeCompany', JSON.stringify(activeCompany));
            // Manter o companyId em sincronia para os helpers existentes
            localStorage.setItem('companyId', activeCompany.id);
        }

        return activeCompany;
    },

    // Obter o token atual
    getToken: (): string | null => {
        return localStorage.getItem('token');
    },

    // Obter o ID da empresa
    getCompanyId: (): string | null => {
        // Se estiver em modo impersonate, extrair companyId do token
        const isImpersonating = localStorage.getItem('isImpersonating') === 'true';

        if (isImpersonating) {
            const token = localStorage.getItem('token');
            if (token) {
                const payload = authService.decodeToken(token);
                if (payload && payload.companyId) {
                    return payload.companyId;
                }
            }
        }

        // Caso contrário, retornar do localStorage normalmente
        return localStorage.getItem('companyId');
    },

    // Obter as informações do usuário logado
    getUser: () => {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    },

    // Decodificar o token JWT para obter o payload
    decodeToken: (token: string): JwtPayload | null => {
        try {
            // Verificar o formato do token (deve ter 3 partes separadas por pontos)
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decodificar a parte do meio (payload)
            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Erro ao decodificar token:', error);
            return null;
        }
    },

    // Verificar se o token está expirado
    isTokenExpired: (token: string): boolean => {
        const payload = authService.decodeToken(token);
        if (!payload || !payload.exp) return true;

        // Verificar expiração - converter para segundos
        const now = Date.now() / 1000;
        return payload.exp < now;
    },

    // Verificar se o usuário está autenticado
    isAuthenticated: (): boolean => {
        const token = localStorage.getItem('token');

        // Se não houver token, não está autenticado
        if (!token) return false;

        try {
            // Verificar se o token é válido e não está expirado
            if (authService.isTokenExpired(token)) {
                console.log('Token expirado, fazendo logout');
                authService.logout();
                return false;
            }

            return true;
        } catch (error) {
            console.error('Erro ao verificar autenticação:', error);
            authService.logout();
            return false;
        }
    },

    // Verificar e-mail através do token de verificação
    verifyEmail: async (token: string): Promise<VerifyEmailResponse> => {
        try {
            const response = await api.post('/api/auth/verify-email', { token });
            return response.data;
        } catch (error) {
            console.error('Erro ao verificar e-mail:', error);
            return {
                success: false,
                message: 'Ocorreu um erro ao verificar seu e-mail. O link pode ter expirado ou ser inválido.'
            };
        }
    },

    // Reenviar e-mail de verificação
    resendVerificationEmail: async (email: string): Promise<VerifyEmailResponse> => {
        try {
            const response = await api.post('/api/auth/resend-verification', { email });
            return response.data;
        } catch (error) {
            console.error('Erro ao reenviar e-mail de verificação:', error);
            return {
                success: false,
                message: 'Ocorreu um erro ao reenviar o e-mail de verificação. Tente novamente mais tarde.'
            };
        }
    },

    // Solicitar recuperação de senha
    forgotPassword: async (email: string): Promise<ForgotPasswordResponse> => {
        try {
            const response = await api.post('/api/auth/forgot-password', { email });
            return response.data;
        } catch (error) {
            console.error('Erro ao solicitar recuperação de senha:', error);
            return {
                success: false,
                message: 'Ocorreu um erro ao solicitar a recuperação de senha. Tente novamente mais tarde.'
            };
        }
    },

    // Reset de senha com token
    resetPassword: async (token: string, newPassword: string): Promise<ForgotPasswordResponse> => {
        try {
            const response = await api.post('/api/auth/reset-password', { token, password: newPassword });
            return response.data;
        } catch (error) {
            console.error('Erro ao redefinir senha:', error);
            return {
                success: false,
                message: 'Ocorreu um erro ao redefinir sua senha. O link pode ter expirado ou ser inválido.'
            };
        }
    },

    // Renovar token JWT para obter informações atualizadas do usuário
    renewToken: async (): Promise<boolean> => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return false;

            const response = await api.post('/api/auth/renew-token', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data && response.data.success) {
                // Atualizar token no localStorage
                localStorage.setItem('token', response.data.data.token);

                // Atualizar informações do usuário se disponíveis
                if (response.data.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.data.user));

                    // Atualizar companyId se disponível
                    if (response.data.data.user.companyId) {
                        localStorage.setItem('companyId', response.data.data.user.companyId);
                    }
                }

                return true;
            }

            return false;
        } catch (error) {
            console.error('Erro ao renovar token:', error);
            return false;
        }
    },

    // Logout - remover dados de autenticação
    logout: (): void => {
        localStorage.clear();

        // Disparar evento de logout para limpar contextos
        window.dispatchEvent(new CustomEvent('authChange', { detail: { type: 'logout' } }));
    }
};

export default authService;
